/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Card, Spin, Row, Col } from 'antd';
import { AccessPeriod } from './AccessFilterHeader';

interface AccessOverviewCardsProps {
  loading?: boolean;
  period?: AccessPeriod;
  cards?: {
    averageAccess: number;
    averageUnit: string;
    averages?: {
      daily?: number;
      monthly?: number;
      yearly?: number;
    };
    totalAccess: number;
    activeUsers: number;
    inactiveUsers: number;
  };
}

export const AccessOverviewCards: React.FC<AccessOverviewCardsProps> = ({
  loading = false,
  period = 'all',
  cards = {
    averageAccess: 0,
    averageUnit: 'lượt',
    totalAccess: 0,
    activeUsers: 0,
    inactiveUsers: 0,
  },
}) => {
  const formatNumber = (num: number) => num.toLocaleString('vi-VN');

  const getCardLabels = () => {
    let avg = 'Lượt truy cập trung bình/ngày';
    switch (period) {
      case 'today':
      case 'last_30_days':
        avg = 'Lượt truy cập trung bình/ngày';
        break;
      case 'this_month':
      case 'last_12_months':
        avg = 'Lượt truy cập trung bình/tháng';
        break;
      case 'this_year':
      case 'last_3_years':
        avg = 'Lượt truy cập trung bình/năm';
        break;
      case 'custom':
      case 'all':
      default:
        avg = 'Lượt truy cập trung bình/ngày';
        break;
    }
    return {
      avg,
      total: 'Tổng lượt truy cập',
      active: 'Số tài khoản hoạt động',
      inactive: 'Số tài khoản không hoạt động',
    };
  };

  const labels = getCardLabels();
  const isMultiAverage = period === 'all' || period === 'custom';

  return (
    <Card css={cardContainerStyle}>
      <Spin spinning={loading}>
        <div css={cardHeaderStyle}>
          Lượt truy cập toàn hệ thống
        </div>

        <div css={cardBodySectionStyle}>
          <Row gutter={[24, 0]}>
            {/* Top-Left: Average Access (Cách A: Liệt kê các mức trung bình đủ điều kiện) */}
            <Col span={12}>
              {isMultiAverage ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div css={metricItemStyle}>
                    <span css={labelStyle}>Lượt truy cập trung bình/ngày</span>
                    <div css={valueWrapperStyle}>
                      <span css={valueStyle}>
                        {formatNumber(cards.averages?.daily ?? cards.averageAccess)}
                      </span>
                      <span css={unitStyle}>lượt</span>
                    </div>
                  </div>
                  {cards.averages?.monthly !== undefined && (
                    <div css={metricItemStyle}>
                      <span css={labelStyle}>Lượt truy cập trung bình/tháng</span>
                      <div css={valueWrapperStyle}>
                        <span css={valueStyle}>{formatNumber(cards.averages.monthly)}</span>
                        <span css={unitStyle}>lượt</span>
                      </div>
                    </div>
                  )}
                  {cards.averages?.yearly !== undefined && (
                    <div css={metricItemStyle}>
                      <span css={labelStyle}>Lượt truy cập trung bình/năm</span>
                      <div css={valueWrapperStyle}>
                        <span css={valueStyle}>{formatNumber(cards.averages.yearly)}</span>
                        <span css={unitStyle}>lượt</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div css={metricItemStyle}>
                  <span css={labelStyle}>{labels.avg}</span>
                  <div css={valueWrapperStyle}>
                    <span css={valueStyle}>{formatNumber(cards.averageAccess)}</span>
                    <span css={unitStyle}>lượt</span>
                  </div>
                </div>
              )}
            </Col>

            {/* Top-Right: Total Access */}
            <Col span={12}>
              <div css={metricItemStyle}>
                <span css={labelStyle}>{labels.total}</span>
                <div css={valueWrapperStyle}>
                  <span css={valueStyle}>{formatNumber(cards.totalAccess)}</span>
                  <span css={unitStyle}>lượt</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div css={cardBodySectionBottomStyle}>
          <Row gutter={[24, 0]}>
            {/* Bottom-Left: Active Users */}
            <Col span={12}>
              <div css={metricItemStyle}>
                <span css={labelStyle}>{labels.active}</span>
                <div css={valueWrapperStyle}>
                  <span css={valueStyle}>{formatNumber(cards.activeUsers)}</span>
                  <span css={unitStyle}>tài khoản</span>
                </div>
              </div>
            </Col>

            {/* Bottom-Right: Inactive Users */}
            <Col span={12}>
              <div css={metricItemStyle}>
                <span css={labelStyle}>{labels.inactive}</span>
                <div css={valueWrapperStyle}>
                  <span css={valueStyle}>{formatNumber(cards.inactiveUsers)}</span>
                  <span css={unitStyle}>tài khoản</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Spin>
    </Card>
  );
};

const cardContainerStyle = css`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: none;
  height: 100%;
  .ant-card-body {
    padding: 0;
  }
`;

const cardHeaderStyle = css`
  text-align: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 15px;
  color: #262626;
`;

const cardBodySectionStyle = css`
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const cardBodySectionBottomStyle = css`
  padding: 24px;
`;

const metricItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const labelStyle = css`
  font-size: 13px;
  color: #8c8c8c;
`;

const valueWrapperStyle = css`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const valueStyle = css`
  font-size: 26px;
  font-weight: 700;
  color: #262626;
  line-height: 1.2;
`;

const unitStyle = css`
  font-size: 15px;
  font-weight: 600;
  color: #262626;
`;
